import Component from './Component';
import BackdropComponent from './BackdropComponent';
import DeviceDetection from "../utils/DeviceDetection";

export default class ModalComponent extends Component {
    get SELECTORS() {
        return {
            main: '.js-modal',
            backdropGroup: '.js-backdrop-group',
            activeDialog: '[role="dialog"]:not(.close)'
        };
    }

    get FOCUSABLE_SELECTORS() {
        return [
            'a[href]:not([disabled])',
            'button:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"]):not([disabled])',
            '[role="button"]:not([disabled])',
            '.js-close-panel', // Specifically add the close panel
            '[contenteditable="true"]:not([disabled])'
        ];
    }

    get CLASSES() {
        return {
            close: 'close',
        }
    }

    get DATA() {
        return {
            modal: 'data-modal',
        }
    }

    constructor() {
        super();
        this.BackdropComponent = new BackdropComponent();
        this.DeviceDetectionUtil = new DeviceDetection();
        this.lastFocusedElement = null;
        this.closedByKeyboard = false;
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.modalTrap = this.modalTrap.bind(this);
        this.closeModalHandler = this.closeModalHandler.bind(this);
        this.eventsBound = false;
    }

    closeModalHandler(e) {
        e.preventDefault();
        // console.log('Close button clicked, this:', this); // Verifica che 'this' sia corretto
        this.closeModal();
    }

    closeModalDeprecated() {
        this.BackdropComponent.close();
    }

    openModalDeprecated(modal, scrollable = false) {
        // console.log('Opening modal:', modal);
        this.BackdropComponent.close();
        this.BackdropComponent.open(modal, scrollable);
        modal.classList.remove(this.CLASSES.close);
        if(modal.id == 'sessionTimeoutPopup') {
            localStorage.clear()
        }
    }

    closeModal() {
        const modal = document.querySelector(this.SELECTORS.activeDialog);
        
        // Restore the original state
        // console.log('Restoring focus to:', this.lastFocusedElement);
        if (this.closedByKeyboard && this.lastFocusedElement) {
            setTimeout(() => {
                this.lastFocusedElement.focus();
                this.lastFocusedElement = null;
                this.closedByKeyboard = false;
            }, 100);
        } else {
            this.lastFocusedElement = null;
            this.closedByKeyboard = false;
        }

        if (!modal) {
          this.BackdropComponent.close();
          return;
        }

        modal.classList.add(this.CLASSES.close);
        modal.setAttribute("aria-hidden", "true");
        document.body.classList.remove("modal-open");

        // Remove event listeners
        document.removeEventListener("keydown", this.handleKeyDown);
        document.removeEventListener("keydown", this.modalTrap);

        // Reset focusable elements
        this.currentFocusableElements = [];

        // console.log('Closing modal');
        this.BackdropComponent.close();
    }

    openModal(modal, scrollable = false) {
        const closeButtons = modal.querySelectorAll('.js-close-panel');
        closeButtons.forEach(button => {
            // Remove any previous listeners to avoid duplicates
            button.removeEventListener('click', this.closeModalHandler);
            // Add the new listener
            button.addEventListener('click', this.closeModalHandler);

            button.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                    this.closedByKeyboard = true;
                }
            });
        });

        // Close other open modals before opening a new one
        this.closeModal();

        // Store the currently focused element before opening the modal
        this.lastFocusedElement = document.activeElement;
        // console.log('Saving focus element:', this.lastFocusedElement);

        // Prepare and open the modal
        this.BackdropComponent.open(modal, scrollable);
        modal.classList.remove(this.CLASSES.close);
        if(modal.id == 'sessionTimeoutPopup') {
            localStorage.clear()
        }

        // Check if there are buttons without a valid tabindex and fix them
        this.fixButtonTabindex(modal);

        // Make the modal accessible and visible
        modal.setAttribute("aria-hidden", "false");
        
        // Set tabindex only if it is not already present
        if (!modal.hasAttribute("tabindex")) {
            modal.setAttribute("tabindex", "-1");
        }
        
        document.body.classList.add("modal-open");
        
        // Get and store focusable elements
        this.currentFocusableElements = this.getFocusableElements(modal);
        
        // Before setting the focus, ensure all CSS transitions are complete
        setTimeout(() => {
            // First set focus on the modal itself for screen readers
            modal.focus();
            
            // Then focus on a sensible interactive element
            if (this.currentFocusableElements.length > 0) {
                const closeButton = modal.querySelector('.js-close-panel');
                if (closeButton) {
                    closeButton.focus();
                } else if (this.currentFocusableElements.length > 1) {
                    // Use the second element (skipping the first in some cases)
                    this.currentFocusableElements[1].focus();
                } else {
                    this.currentFocusableElements[0].focus();
                }
            }
        }, 100);
        
        // Add event listeners for keyboard navigation
        document.addEventListener("keydown", this.handleKeyDown);
        document.addEventListener("keydown", this.modalTrap);
        
        // Fallback if focus is still outside modal after a longer delay
        setTimeout(() => {
            if (!modal.contains(document.activeElement) && this.currentFocusableElements.length > 0) {
                try {
                    document.activeElement.blur();
                    const closeButton = modal.querySelector('.js-close-panel');
                    if (closeButton) {
                        closeButton.focus();
                    } else {
                        this.currentFocusableElements[0].focus();
                    }
                } catch (e) {
                    // If it fails, continue silently
                }
            }
        }, 500);
    }
    
    // Method to fix tabindex issues on buttons
    fixButtonTabindex(modal) {
        // Find all buttons or interactive elements that might not have tabindex
        const interactiveElements = modal.querySelectorAll('.js-close-panel, .btn');
        interactiveElements.forEach(el => {
            // If it's a span or div without tabindex, make it focusable
            if ((el.tagName === 'SPAN' || el.tagName === 'DIV') && 
                (!el.hasAttribute('tabindex') || el.getAttribute('tabindex') === '-1')) {
                el.setAttribute('tabindex', '0');
                // Also add a KeyDown listener to make the element activatable with Enter or Space
                el.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                        e.preventDefault();
                        el.click();
                    }
                });
            }
        });
    }

    getFocusableElements(modal) {
      // Improved selector for focusable elements
      const elements = Array.from(modal.querySelectorAll(this.FOCUSABLE_SELECTORS.join(', '))).filter(el => {
          // Filter hidden elements
          const style = window.getComputedStyle(el);
          const isVisible = !(
              style.display === 'none' || 
              style.visibility === 'hidden' || 
              el.offsetParent === null || 
              (el.classList && el.classList.contains('hide'))
          );
          
          // If it's an element that should be focusable but is not,
          // such as a SPAN with button role, make it focusable
          if (isVisible && 
              (el.classList.contains('button__close') || el.classList.contains('js-close-panel')) && 
              !el.hasAttribute('tabindex')) 
          {
              el.setAttribute('tabindex', '0');
          }
          
          return isVisible;
      });
      
      return elements;
    }

    handleKeyDown(event) {
        // If the user presses Escape, simulate a click on the close button
        if (event.key === "Escape") {
            this.closedByKeyboard = true;
            const modal = document.querySelector(this.SELECTORS.activeDialog);
            if (modal) {
                // Modify the Escape key behavior in modals to simulate a click on the close 
                // button (.js-close-panel) instead of directly closing the modal. This fixes 
                // the issue in the address input modal where pressing Escape was saving the 
                // data instead of canceling the operation.

                // Find the close button in the current modal
                const closeButton = modal.querySelector('.js-close-panel');
                if (closeButton) {
                    // Simulate a click on the close button
                    closeButton.click();
                } else {
                    // Fallback: if the button does not exist, use the standard method
                    this.closeModal();
                }
            } else {
                this.closeModal();
            }
      }
    }

    modalTrap(event) {
        // Handle only Tab events
        // console.log('Entered modalTrap');
        if (event.key !== "Tab") {
          // console.log('Not a Tab key - exiting');
          return;
        }

        const modal = document.querySelector(this.SELECTORS.activeDialog);
        if (!modal) {
            // console.log('No modal found - exiting');
            return;
        }

        // Use stored elements instead of reloading new ones each time
        this.currentFocusableElements = this.getFocusableElements(modal);
        
        if (this.currentFocusableElements.length === 0) {
            return;
        }

        const firstElement = this.currentFocusableElements[0];
        const lastElement = this.currentFocusableElements[this.currentFocusableElements.length - 1];

        // Specific check for the Annulla button
        const isOnLastElement = document.activeElement === lastElement || 
            (document.activeElement.classList && 
            document.activeElement.classList.contains('button__close'));

        // Handle forward and backward focus cycling
        if (event.shiftKey && document.activeElement === firstElement) {
            // If the user Shift+Tabs from the first element, go to the last
            event.preventDefault();
            lastElement.focus();
            return;
        } else if (!event.shiftKey && isOnLastElement) {
            // If the user Tabs from the last element, go to the first
            event.preventDefault();
            firstElement.focus();
            return;
        }

        // Ensure the focus is within the modal
        if (!modal.contains(document.activeElement)) {
            event.preventDefault();
            firstElement.focus();
        }
    }

    bindEvents() {
        if (this.eventsBound) return;
        
        this.events.on('click', this.SELECTORS.main, (e) => {
            e.preventDefault();
            const element = e.target.matches(this.SELECTORS.main) ? e.target.getAttribute(this.DATA.modal) : e.target.closest(this.SELECTORS.main).getAttribute(this.DATA.modal);
            // console.log('Modal trigger clicked:', element);
            if (!!element) {
                this.openModal(document.getElementById(element));    
            }
        });

        // ADD KEYBOARD SUPPORT FOR ENTER AND SPACEBAR
        /* document.querySelectorAll(this.SELECTORS.main).forEach(trigger => {
            trigger.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                    e.preventDefault();
                    const modalId = trigger.getAttribute(this.DATA.modal);
                    if (modalId) {
                        this.openModal(document.getElementById(modalId));
                    }
                }
            });
        }); */

        if (this.DeviceDetectionUtil.detection().isMobile) {
            window.addEventListener('resize', () => {
                let vh = window.innerHeight * 0.01;
                document.documentElement.style.setProperty('--vh', `${vh}px`);
            });
        }
        
        this.eventsBound = true;
    }

    render() {
        this.log('render Modal');
        this.bindEvents();
        
        // Check if there is an open modal when the page loads
        const modal = document.querySelector(this.SELECTORS.activeDialog);
        if (modal) {
            // Store focusable elements and trap the focus
            this.currentFocusableElements = this.getFocusableElements(modal);
            if (this.currentFocusableElements.length > 0) {
                setTimeout(() => {
                    this.currentFocusableElements[0].focus();
                }, 100);
            }
            document.addEventListener("keydown", this.modalTrap);
        }
    }
}
